import { useState, useEffect, useCallback, Component, lazy, Suspense } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { API } from "./lib/helpers";
import { RefreshCw } from "lucide-react";
import { logError, setupAxiosErrorInterceptor, setupGlobalErrorHandlers } from "./lib/errorLogger";
import NetworkOfflineToast from "./components/NetworkOfflineToast";

// Critical pages — eagerly loaded
import AuthPage from "./pages/AuthPage";
import LandingPage from "./pages/LandingPage";
import TopDealsPage from "./pages/TopDealsPage";
import DashboardPage from "./pages/DashboardPage";
import ScrollToTop from "./components/ScrollToTop";
import MobileStickyBar from "./components/MobileStickyBar";
import UpsellPopupManager from "./components/UpsellPopupManager";

// Lazy-loaded pages — code-split for performance
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const CashFlowCalculatorPage = lazy(() => import("./pages/CashFlowCalculatorPage"));
const CityDealsPage = lazy(() => import("./pages/CityDealsPage"));
const PricingPage = lazy(() => import("./pages/PricingPage"));
const BillingPage = lazy(() => import("./pages/BillingPage"));
const BusinessInquiryPage = lazy(() => import("./pages/BusinessInquiryPage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const SharedComparisonPage = lazy(() => import("./pages/SharedComparisonPage"));
const BestDealsPage = lazy(() => import("./pages/BestDealsPage"));
const BestCashFlowDealsPage = lazy(() => import("./pages/BestCashFlowDealsPage"));
const TopMultifamilyDealsPage = lazy(() => import("./pages/TopMultifamilyDealsPage"));
const BestDealsUnder300kPage = lazy(() => import("./pages/BestDealsUnder300kPage"));
const TopForeclosureDealsPage = lazy(() => import("./pages/TopForeclosureDealsPage"));
const TopBRRRRDealsPage = lazy(() => import("./pages/TopBRRRRDealsPage"));
const BestMarketsForBeginnersPage = lazy(() => import("./pages/BestMarketsForBeginnersPage"));
const BestRentalMarketsPage = lazy(() => import("./pages/BestRentalMarketsPage"));
const TrendingMarketsPage = lazy(() => import("./pages/TrendingMarketsPage"));
const StateDealPage = lazy(() => import("./pages/StateDealPage"));
const SharedDealPage = lazy(() => import("./pages/SharedDealPage"));
const LegalPage = lazy(() => import("./pages/LegalPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const BestRealEstateDealsPage = lazy(() => import("./pages/BestRealEstateDealsPage"));
const FeaturedCityPage = lazy(() => import("./pages/FeaturedCityPage"));
const DealPage = lazy(() => import("./pages/DealPage"));
const HowScoringWorksPage = lazy(() => import("./pages/HowScoringWorksPage"));
const BlogIndexPage = lazy(() => import("./pages/BlogIndexPage"));
const BlogPostPage = lazy(() => import("./pages/BlogPostPage"));
const SEOArticlePage = lazy(() => import("./pages/SEOArticlePage"));
const UnsubscribePage = lazy(() => import("./pages/UnsubscribePage"));
const WhatIsRealixDataPage = lazy(() => import("./pages/WhatIsRealixDataPage"));
const RealixDataReviewPage = lazy(() => import("./pages/RealixDataReviewPage"));
const RealixDataVsRealDataPage = lazy(() => import("./pages/RealixDataVsRealDataPage"));
const RealixDataAlternativesPage = lazy(() => import("./pages/RealixDataAlternativesPage"));
const TripwireSuccessPage = lazy(() => import("./pages/TripwireSuccessPage"));
const ActivatePage = lazy(() => import("./pages/ActivatePage"));
const ZipCodePage = lazy(() => import("./pages/ZipCodePage"));
const CreateReportPage = lazy(() => import("./pages/CreateReportPage"));
const ReportPage = lazy(() => import("./pages/ReportPage"));

// SEC-004: auth is cookie-only.
// The httpOnly `session_token` cookie is set server-side on login /
// register / activate. `withCredentials: true` makes the browser attach
// it to every API call automatically. The token is NEVER read from or
// written to `localStorage` — that would defeat the httpOnly protection
// (JS can read localStorage; XSS could exfiltrate the session).
axios.defaults.withCredentials = true;

// Error Boundary for catching render crashes
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error, info) {
    logError({
      errorType: "render_error",
      message: error.message,
      stack: `${error.stack}\n\nComponent Stack:${info.componentStack}`,
      page: window.location.pathname,
    });
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-background flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold mb-2">Something went wrong</h1>
            <p className="text-muted-foreground mb-4">Please refresh the page and try again.</p>
            <button onClick={() => window.location.reload()} className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700">
              Refresh Page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Auth Context (Render Prop)
const AuthContext = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Handle session expiry: show toast, clear state, redirect
  const handleSessionExpired = useCallback(() => {
    setUser(null);
    // Only show toast if we were previously logged in
    if (window.location.pathname.startsWith("/dashboard") ||
        window.location.pathname.startsWith("/settings") ||
        window.location.pathname.startsWith("/billing")) {
      toast.error("Session expired — please log in again", { duration: 4000 });
      setTimeout(() => {
        window.location.href = "/auth";
      }, 1500);
    }
  }, []);

  useEffect(() => {
    // Setup error handlers once
    setupGlobalErrorHandlers();
    setupAxiosErrorInterceptor(handleSessionExpired);
  }, [handleSessionExpired]);

  useEffect(() => {
    // Always hit /auth/me — the browser sends the httpOnly cookie for
    // users on the new flow, and the axios interceptor adds the Bearer
    // header for users still on the legacy localStorage flow. Either
    // one authenticates the request; a 401 just means logged out.
    axios
      .get(`${API}/auth/me`)
      .then((r) => setUser(r.data))
      .catch(() => {
        // Best-effort cleanup of any legacy token left over from the
        // pre-cookie release. Safe to remove after the next full deploy.
        localStorage.removeItem("session_token");
        localStorage.removeItem("user");
      })
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    // Backend sets the httpOnly session_token cookie on this response.
    // SEC-004: we NO LONGER mirror the token into localStorage — the
    // cookie is the sole source of auth.
    setUser(res.data);
    return res.data;
  };

  const logout = async () => {
    // Server-side session revocation — clears both the DB row and the
    // httpOnly cookie so the token can't be replayed after logout.
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (err) {
      // Non-fatal — even if the request failed, we still wipe local state.
      console.debug("[auth] logout request failed:", err);
    }
    // Best-effort cleanup of any lingering legacy token.
    localStorage.removeItem("session_token");
    localStorage.removeItem("user");
    setUser(null);
  };

  return (
    <div className="bg-background text-foreground min-h-screen">
      {children({ user, setUser, loading, login, logout })}
    </div>
  );
};

// App Router
function AppRouter() {
  return (
    <AuthContext>
      {({ user, setUser, loading, login, logout }) => {
        if (loading) {
          return (
            <div className="min-h-screen bg-background flex items-center justify-center">
              <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          );
        }
        const isAuthenticated = !!user;
        return (
          <>
            <ScrollToTop />
            <Toaster theme="system" position="bottom-right" richColors />
            <NetworkOfflineToast />
            <MobileStickyBar isAuthenticated={isAuthenticated} />
            <UpsellPopupManager isAuthenticated={isAuthenticated} />
            <Suspense fallback={<div className="min-h-screen bg-background flex items-center justify-center"><RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" /></div>}>
            <Routes>
              <Route
                path="/"
                element={
                  <LandingPage
                    login={login}
                    isAuthenticated={isAuthenticated}
                  />
                }
              />
              <Route
                path="/auth"
                element={
                  isAuthenticated ? (
                    <Navigate to="/dashboard" replace />
                  ) : (
                    <AuthPage login={login} setUser={setUser} />
                  )
                }
              />
              <Route
                path="/dashboard"
                element={
                  <DashboardPage
                    user={user}
                    logout={logout}
                    isAuthenticated={isAuthenticated}
                  />
                }
              />
              <Route path="/top-deals" element={<TopDealsPage user={user} isAuthenticated={isAuthenticated} />} />
              <Route path="/best-rental-property-deals" element={<BestDealsPage />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route
                path="/settings"
                element={
                  isAuthenticated ? (
                    <SettingsPage user={user} setUser={setUser} />
                  ) : (
                    <Navigate to="/auth" replace />
                  )
                }
              />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/tripwire/success" element={<TripwireSuccessPage />} />
              <Route path="/tripwire/cancel" element={<Navigate to="/dashboard?tripwire=cancel" replace />} />
              <Route path="/activate" element={<ActivatePage />} />
              <Route path="/compare/:shareId" element={<SharedComparisonPage />} />
              <Route path="/billing" element={<BillingPage />} />
              <Route path="/business" element={<BusinessInquiryPage />} />
              <Route path="/legal/:slug" element={<LegalPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/best-real-estate-investment-deals" element={<BestRealEstateDealsPage />} />
              <Route path="/deals/:slug" element={<FeaturedCityPage />} />
              <Route path="/zip/:zipcode" element={<ZipCodePage />} />
              <Route path="/deal/:slug" element={<DealPage />} />
              <Route path="/create-report" element={<CreateReportPage />} />
              <Route path="/report/:reportId" element={<ReportPage />} />
              <Route path="/how-scoring-works" element={<HowScoringWorksPage />} />
              <Route path="/faq" element={<BlogIndexPage />} />
              <Route path="/faq/:slug" element={<BlogPostPage />} />
              {/* Top-level SEO content pages */}
              <Route path="/how-to-analyze-rental-properties" element={<SEOArticlePage slug="how-to-analyze-rental-properties" />} />
              <Route path="/1-percent-rule-real-estate" element={<SEOArticlePage slug="1-percent-rule-real-estate" />} />
              <Route path="/cash-flow-real-estate-explained" element={<SEOArticlePage slug="cash-flow-real-estate-explained" />} />
              <Route path="/cash-flow-calculator-real-estate" element={<CashFlowCalculatorPage />} />
              <Route path="/what-is-a-good-cap-rate" element={<SEOArticlePage slug="what-is-a-good-cap-rate" />} />
              <Route path="/best-rental-markets-tampa-jacksonville-atlanta" element={<SEOArticlePage slug="best-rental-markets-tampa-jacksonville-atlanta" />} />
              {/* Redirect old blog URLs to FAQ (301-equivalent for SPA) */}
              <Route path="/blog" element={<Navigate to="/faq" replace />} />
              <Route path="/blog/1-percent-rule-real-estate-explained" element={<Navigate to="/1-percent-rule-real-estate" replace />} />
              <Route path="/blog/how-to-analyze-rental-properties" element={<Navigate to="/how-to-analyze-rental-properties" replace />} />
              <Route path="/blog/:slug" element={<Navigate to="/faq" replace />} />
              <Route path="/unsubscribe" element={<UnsubscribePage />} />
              {/* Branded SEO Pages */}
              <Route path="/what-is-realixdata" element={<WhatIsRealixDataPage />} />
              <Route path="/realixdata-review" element={<RealixDataReviewPage />} />
              <Route path="/realixdata-vs-realdata" element={<RealixDataVsRealDataPage />} />
              <Route path="/realixdata-alternatives" element={<RealixDataAlternativesPage />} />
              {/* SEO Deal Pages */}
              <Route path="/best-cash-flow-deals" element={<BestCashFlowDealsPage user={user} isAuthenticated={isAuthenticated} />} />
              <Route path="/top-multifamily-deals" element={<TopMultifamilyDealsPage user={user} isAuthenticated={isAuthenticated} />} />
              <Route path="/best-deals-under-300k" element={<BestDealsUnder300kPage user={user} isAuthenticated={isAuthenticated} />} />
              <Route path="/top-foreclosure-deals" element={<TopForeclosureDealsPage user={user} isAuthenticated={isAuthenticated} />} />
              <Route path="/top-brrrr-properties" element={<TopBRRRRDealsPage user={user} isAuthenticated={isAuthenticated} />} />
              <Route path="/best-markets-for-beginners" element={<BestMarketsForBeginnersPage user={user} isAuthenticated={isAuthenticated} />} />
              <Route path="/best-rental-markets" element={<BestRentalMarketsPage user={user} isAuthenticated={isAuthenticated} />} />
              <Route path="/trending-markets" element={<TrendingMarketsPage user={user} isAuthenticated={isAuthenticated} />} />
              <Route path="/best-deals-in/:stateSlug" element={<StateDealPage user={user} isAuthenticated={isAuthenticated} />} />
              <Route path="/admin" element={
                isAuthenticated && user?.email === "austinreeseajr99@gmail.com" ? (
                  <AdminDashboard />
                ) : (
                  <Navigate to="/dashboard" replace />
                )
              } />
              <Route
                path="/investment-properties/:slug"
                element={<CityDealsPage />}
              />
              {/* Redirects for removed pages */}
              <Route path="/commercial" element={<Navigate to="/dashboard" replace />} />
              <Route path="/compare" element={<Navigate to="/dashboard" replace />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            </Suspense>
          </>
        );
      }}
    </AuthContext>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
